import Modal from "react-modal";
import React, {useEffect, useState} from "react";

import './Program.generate.zip.report.dialog.scss';
import {useTranslation} from "react-i18next";
import {useLazyQuery, useSubscription} from "@apollo/client";
import gqlSchemaLibrary from "../../services/graphql.schemas";
import { DayPicker, addToRange } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import moment from "moment-timezone";
import SpinnerLoader from "../../visuals/SpinnerLoader";
import {ModalLog} from "../../services/interactions/elements/modal";
import AuditTrail, {PAGES} from "../../services/interactions/audit.trail";

const customStyles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        border: "none",
        padding: "0px",
        boxShadow: "0 3px 8px 0 rgba(0, 0, 0, 0.38)",
    }
};
Modal.setAppElement('#root');

const _Modal = ModalLog(Modal);

function ProgramGenerateZipReportsDialog({currentUser, program, multipleReports, selectedParticipants, selectedPatient, openModal, closeModal}) {
    const {t} = useTranslation(['common', 'teams', 'reports']);
    const [__submitting, __setSubmitting] = useState(false);
    const [showLabel, setShowLabel] = useState(false);
    const [error, setError] = useState('');
    const maxDays = 14;

    const [state, setState] = useState({from: undefined, to: undefined});
    const [disabledDays, setDisabledDays] = useState([
        {
            after: new Date(),
            before: new Date(3000, 1, 1),
        }
    ]);

    const [generateBioReports] = useLazyQuery(gqlSchemaLibrary.programs.query.getProgramBioReports, {
        onCompleted: (onData) => {
            setTimeout(() => {
                setShowLabel(true);
                if (onData.getProgramBioReports.code !== 200) {
                    let msg = onData.getProgramBioReports.msg;
                    if (msg === 'process-running') {
                        msg = t('reports:generating_report');
                    } else if (msg === 'empty-devices') {
                        msg = 'Selected participant(s) do not have device(s)';
                    } else if (msg === 'empty-reports') {
                        msg = 'Selected participant(s) do not have BioReport(s)';
                    }
                    setError(msg);
                }
            }, 500);
        },
        onError: (error) => {
            setShowLabel(true);
            __setSubmitting(false);
            setError('Error Generate Zip');
        },
        fetchPolicy: 'cache-and-network'
    });

    useSubscription(gqlSchemaLibrary.programs.subscriptions.reportEmitter, {
        variables: {
            hash: currentUser._id
        },
        onData: ({ data }) => {
            const data_ = data.data.reportEmitter.data;
            if (data_) {
                if (data_ === 'empty-devices' || data_ === 'empty-bio-reports'){
                    setError('No Bioreport(s) available in this period of time for the participant(s) selected');
                }
            }
        }
    });

    useEffect(() => {
        if (state.from) {
            let _start = moment(state.from);
            let _end = moment(new Date());
            let duration = moment.duration(_end.diff(_start));
            let days = Math.ceil(duration.asDays());
            if (days > maxDays) {
                days = maxDays;
            }
            let maxDate = moment(state.from).add(days, "days");
            if (maxDate > _end) {
                maxDate = _end;
            }
            const _disabledDays = [
                {
                    after: new Date(2000, 1, 1),
                    before: state.from,
                },
                {
                    after: maxDate.toDate(),
                    before: new Date(3000, 1, 1),
                }];
            setDisabledDays(_disabledDays);
        }
    },[state])

    const closeModalEmpty = () => {
        AuditTrail.sendLog(PAGES.PROGRAM_PARTICIPANTS, {tag: 'button', event: 'closeModal', action: 'button close modal', program_id: program? program._id : null});
        setShowLabel(false);
        __setSubmitting(false);
        setError('');
        resetDatePicker();
        closeModal();
    }

    const onSave = () => {
        if (isDisabled() || __submitting) {
            return;
        }
        __setSubmitting(true);
        const psIds = multipleReports? selectedParticipants.map((p) => p._id) : [selectedPatient._id];
        generateBioReports({
            variables: {
                hash: currentUser._id,
                programId: program._id,
                participantIds: psIds,
                startDate: state.from.getTime(),
                endDate: state.to.getTime(),
                timeZone: moment.tz.guess()
            }
        });
    }

    const isDisabled = () => {
        return !state.from || !state.to;
    }

    const onDayClick = (day, modifiers = {}) => {
        if (modifiers.disabled) {
            return;
        }
        if (from && to && day >= from && day <= to) {
            resetDatePicker();
            return;
        }
        const range = addToRange(day, state);
        const _from = range.from;
        const _to = range.to;
        _from.setHours(0);
        _from.setMinutes(0);
        _from.setSeconds(0);
        if (_to) {
            _to.setHours(23);
            _to.setMinutes(59);
            _to.setSeconds(59);
        }
        setState(range);
    }

    const getDateFormat = (d) => {
        return moment(d).format('MM/DD/YYYY');
    }

    const resetDatePicker = () => {
        setState({from: undefined, to: undefined});
        setDisabledDays([{
            after: new Date(),
            before: new Date(3000, 1, 1),
        }]);
    }

    const { from, to } = state;
    const modifiers = { start: from, end: to };

    return (
        <_Modal
        isOpen={!!openModal}
        style={customStyles}
        contentLabel="Add users to participants"
        label="ProgramGenerateReportDialog"
        event={PAGES.PROGRAM_PARTICIPANTS}
        eventParams={{program_id: program? program._id : null}}>
            <div id="ProgramDownloadZipDialog">
                <div className="__MembersTitle">
                    <h3>Generate BioReport(s)</h3>
                </div>
                <div className="__Container">
                    {
                        __submitting? <div className="message">
                            {
                                showLabel? <span>
                                    {
                                        error? error : 'You will receive an email once the zip file is available'
                                    }
                                </span> : <SpinnerLoader/>
                            }
                            </div> :
                            <div className="_datePicker">
                                <div className="_dateLabel">
                                    From: <b>{state.from? getDateFormat(state.from) : '--'}</b>
                                    &nbsp;To: <b>{state.to? getDateFormat(state.to) : '--'}</b>
                                </div>
                                <DayPicker
                                  className="Selectable"
                                  numberOfMonths={1}
                                  onDayClick={onDayClick}
                                  selectedDays={[from, { from, to }]}
                                  modifiers={modifiers}
                                  disabledDays={disabledDays}
                                />
                                <span className="_resetDays" onClick={resetDatePicker}>Reset dates</span>
                            </div>
                    }
                    <div className="containerButtons">
                        {
                            __submitting? (showLabel? <button className="primary green" onClick={closeModalEmpty}>
                                Ok
                            </button> : '') :
                                <>
                                    <button className="primary gray" onClick={closeModalEmpty}>
                                        {t('cancel')}
                                    </button>
                                    <button className="primary green" onClick={onSave} disabled={isDisabled()}>
                                        Generate Report
                                    </button>
                                </>
                        }
                        </div>
                </div>
            </div>
        </_Modal>
    );
}

export default ProgramGenerateZipReportsDialog;
